import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BONUS_ACTIVITIES, BonusActivity } from '../data/bonusActivities';
import '../styles/BonusActivitiesModal.css';

interface BonusActivitiesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TutorialVariant = 'teacher' | 'student';

type TutorialPaths = {
  teacher: string;
  student: string;
};

type TutorialUrls = Partial<Record<TutorialVariant, string>>;

type HoverPreviewState = {
  activityId: string;
  anchorRect: { left: number; top: number; right: number; bottom: number; width: number; height: number };
};

// Firebase Storage object paths for tutorial videos (videos/Tutorials/*)
const TUTORIAL_VIDEO_PATHS_BY_ACTIVITY_ID: Partial<Record<string, TutorialPaths>> = {
  // Rhythm Ball
  'rhythm-ball': {
    teacher: 'videos/Tutorials/OG_Rhythm Ball_Demo.mp4',
    student: 'videos/Tutorials/OG_STUDENTS_BALL GAME.mp4',
  },
  // Body Shapes (aka statue game)
  'body-shapes': {
    teacher: 'videos/Tutorials/OG_TEACHER_STATUE GAME.mp4',
    student: 'videos/Tutorials/OG_STUDENTS_STATUES.mp4',
  },
  // Rhythm Chairs
  'rhythm-chairs': {
    teacher: 'videos/Tutorials/OG_Rhythm Chairs_Demo.mp4',
    student: 'videos/Tutorials/OG_STUDENT_CHAIR GAME.mp4',
  },
};

const BonusActivitiesModal: React.FC<BonusActivitiesModalProps> = ({ isOpen, onClose }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoverPreview, setHoverPreview] = useState<HoverPreviewState | null>(null);
  const [tutorialUrlsByActivityId, setTutorialUrlsByActivityId] = useState<Record<string, TutorialUrls>>({});
  const [activeTutorialVariant, setActiveTutorialVariant] = useState<TutorialVariant>('teacher');
  const hoverVideoRef = useRef<HTMLVideoElement | null>(null);

  const selected = useMemo<BonusActivity | null>(() => {
    if (!selectedId) return null;
    return BONUS_ACTIVITIES.find(a => a.id === selectedId) || null;
  }, [selectedId]);

  // Reset tutorial variant when switching activities
  useEffect(() => {
    setActiveTutorialVariant('teacher');
  }, [selectedId]);

  // Load tutorial video download URLs from Firebase Storage (once per modal open)
  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;

    (async () => {
      try {
        const { getStorage, ref, getDownloadURL } = await import('firebase/storage');
        const storage = getStorage();

        const entries = Object.entries(TUTORIAL_VIDEO_PATHS_BY_ACTIVITY_ID) as Array<[string, TutorialPaths]>;
        const next: Record<string, TutorialUrls> = {};

        await Promise.all(entries.map(async ([activityId, paths]) => {
          const out: TutorialUrls = {};
          await Promise.all((Object.keys(paths) as TutorialVariant[]).map(async (variant) => {
            try {
              const url = await getDownloadURL(ref(storage, paths[variant]));
              out[variant] = url;
            } catch (e) {
              console.warn('Failed to load tutorial video URL', { activityId, variant, path: paths[variant] }, e);
            }
          }));
          next[activityId] = out;
        }));

        if (mounted) setTutorialUrlsByActivityId(next);
      } catch (e) {
        console.error('Failed to initialize Firebase Storage for tutorial videos', e);
      }
    })();

    return () => { mounted = false; };
  }, [isOpen]);

  // Drive popup hover preview video playback
  useEffect(() => {
    const video = hoverVideoRef.current;
    if (!video) return;

    const url = hoverPreview ? tutorialUrlsByActivityId[hoverPreview.activityId]?.teacher : null;

    if (!url) {
      try {
        video.pause();
        video.removeAttribute('src');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (video as any).load?.();
      } catch {
        // ignore
      }
      return;
    }

    try {
      // Ensure muted is set before attempting autoplay
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.src = url;
      video.currentTime = 0;
      video.play().catch(() => {
        // Autoplay may still be blocked in some environments; keeping controls hidden is fine.
      });
    } catch {
      // ignore
    }
  }, [hoverPreview, tutorialUrlsByActivityId]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const selectedTutorialUrls = selected ? tutorialUrlsByActivityId[selected.id] : undefined;
  const selectedTutorialUrl = selectedTutorialUrls?.[activeTutorialVariant]
    || selectedTutorialUrls?.teacher
    || selectedTutorialUrls?.student
    || null;

  if (!isOpen) return null;

  const computeHoverPopupStyle = (anchor: HoverPreviewState['anchorRect']) => {
    const popupW = 360;
    const popupH = Math.round(popupW * 9 / 16);
    const gap = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Prefer right side; if not enough space, use left; otherwise center-ish
    let left = anchor.right + gap;
    if (left + popupW > vw - 12) left = anchor.left - gap - popupW;
    if (left < 12) left = Math.max(12, Math.min(vw - popupW - 12, anchor.left + (anchor.width - popupW) / 2));

    // Vertically center on the card, clamp to viewport
    let top = anchor.top + (anchor.height - popupH) / 2;
    top = Math.max(12, Math.min(vh - popupH - 12, top));

    return { left, top, width: popupW, height: popupH };
  };

  return (
    <div className="bonus-activities-modal-overlay" onClick={handleBackdropClick}>
      <div className="bonus-activities-modal" role="dialog" aria-modal="true">
        <div className="bonus-activities-modal-header">
          <div className="bonus-activities-header-content">
            <div className="bonus-activities-header-left">
              {selected ? (
                <button
                  type="button"
                  className="bonus-activities-back"
                  onClick={() => setSelectedId(null)}
                  aria-label="Back to activity list"
                >
                  ←
                </button>
              ) : (
                <span className="bonus-activities-hero-emoji">🏃</span>
              )}
              <h2>{selected ? `${selected.emoji} ${selected.title}` : 'Bonus Activities'}</h2>
            </div>
            <button
              type="button"
              className="bonus-activities-close"
              onClick={onClose}
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
          {!selected && (
            <div className="bonus-activities-subtitle">
              Quick, no-prep(ish) activities you can run anytime to build musical skills and classroom energy.
            </div>
          )}
        </div>

        <div className="bonus-activities-modal-content">
          {!selected ? (
            <div className="bonus-activities-grid" role="list">
              {BONUS_ACTIVITIES.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="bonus-activity-card"
                  onClick={() => setSelectedId(a.id)}
                  onMouseEnter={(e) => {
                    const url = tutorialUrlsByActivityId[a.id]?.teacher;
                    if (!url) return;
                    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setHoverPreview({
                      activityId: a.id,
                      anchorRect: { left: r.left, top: r.top, right: r.right, bottom: r.bottom, width: r.width, height: r.height },
                    });
                  }}
                  onMouseMove={(e) => {
                    // Keep the popup pinned if the modal scrolls or layout shifts
                    const el = e.currentTarget as HTMLElement | null;
                    if (!el) return;
                    const r = el.getBoundingClientRect();
                    const rect = { left: r.left, top: r.top, right: r.right, bottom: r.bottom, width: r.width, height: r.height };
                    setHoverPreview((prev) => {
                      if (!prev || prev.activityId !== a.id) return prev;
                      return { activityId: a.id, anchorRect: rect };
                    });
                  }}
                  onMouseLeave={() => setHoverPreview((prev) => (prev?.activityId === a.id ? null : prev))}
                  role="listitem"
                >
                  <div className="bonus-activity-card-emoji">{a.emoji}</div>
                  <div className="bonus-activity-card-title">{a.title}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="bonus-activity-detail">
              {selectedTutorialUrl && (
                <div className="bonus-activity-tutorial">
                  <div className="bonus-activity-tutorial-head">
                    <h3>Tutorial</h3>
                    <div className="bonus-activity-tutorial-tabs" role="tablist" aria-label="Tutorial variant">
                      <button
                        type="button"
                        className={`bonus-activity-tutorial-tab ${activeTutorialVariant === 'teacher' ? 'is-active' : ''}`}
                        onClick={() => setActiveTutorialVariant('teacher')}
                        role="tab"
                        aria-selected={activeTutorialVariant === 'teacher'}
                      >
                        Teacher
                      </button>
                      <button
                        type="button"
                        className={`bonus-activity-tutorial-tab ${activeTutorialVariant === 'student' ? 'is-active' : ''}`}
                        onClick={() => setActiveTutorialVariant('student')}
                        role="tab"
                        aria-selected={activeTutorialVariant === 'student'}
                      >
                        Student
                      </button>
                    </div>
                  </div>
                  <video
                    key={selectedTutorialUrl}
                    className="bonus-activity-tutorial-video"
                    src={selectedTutorialUrl}
                    controls
                    playsInline
                    preload="metadata"
                  />
                </div>
              )}

              <div className="bonus-activity-summary">
                <strong>Summary:</strong> {selected.summary}
              </div>

              <div className="bonus-activity-section">
                <h3>Materials</h3>
                {selected.materials.length > 0 ? (
                  <ul className="bonus-activity-materials">
                    {selected.materials.map((m, idx) => (
                      <li key={idx}>{m}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="bonus-activity-empty">No materials listed.</div>
                )}
              </div>

              <div className="bonus-activity-section">
                <h3>Instructions</h3>
                {selected.instructions.length > 0 ? (
                  <ol className="bonus-activity-instructions">
                    {selected.instructions.map((step, idx) => (
                      <li key={idx}>{step}</li>
                    ))}
                  </ol>
                ) : (
                  <div className="bonus-activity-empty">No instructions provided.</div>
                )}
              </div>
            </div>
          )}
        </div>

        {!selected && hoverPreview && tutorialUrlsByActivityId[hoverPreview.activityId]?.teacher && (
          <div
            className="bonus-activity-hover-preview"
            style={computeHoverPopupStyle(hoverPreview.anchorRect)}
            aria-hidden="true"
          >
            <video
              ref={hoverVideoRef}
              className="bonus-activity-hover-preview-video"
              muted
              playsInline
              preload="auto"
            />
            <div className="bonus-activity-hover-preview-badge">Preview</div>
          </div>
        )}

        <div className="bonus-activities-modal-footer">
          {selected && (
            <button type="button" className="bonus-activities-secondary" onClick={() => setSelectedId(null)}>
              Back to list
            </button>
          )}
          <button type="button" className="bonus-activities-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BonusActivitiesModal;


